import { type FetchBaseQueryError, createApi } from '@reduxjs/toolkit/query/react';
import type { IProductReview } from 'modules/common/products/interfaces/products';

import { API_URL } from '../../../common/constants/api-url';
import { Product } from '../../../common/interfaces/agg-page';
import { utilProductDataPreparing } from '../../../common/utils';
import { IProductAiImages } from '../../../modules/pages/page-form/page-form';
import { getBaseQuery } from '../base-query';
import { PageFormProduct } from '../pages-api/pages-api-types';
import { productLinksApiSlice } from '../product-links-api/product-links-api';
import {
  AttachProductContentReqBody, GetFullProductDataReqBody,
  GetProductByAsinReqBody,
  GetProductByAsinResBody, GetProductByIdReqBody, GetProductByIdResBody,
  GetProductContentCopiesResBody,
  SubmitProductContentReqBody,
  SubmitProductContentResBody,
  UpdateProductReqBody,
  UpdateProductReviewsReqBody,
} from './products-api-types';

export const productsApiSlice = createApi({
  baseQuery: getBaseQuery({ baseUrl: API_URL }),
  endpoints: (builder) => ({
    attachProductContent: builder.mutation<null, AttachProductContentReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/agg-pages/products/options',
      }),
    }),
    getProductByAsin: builder.query<GetProductByAsinResBody, GetProductByAsinReqBody>({
      query: ({
        asin,
        options,
      }) => {
        const { region, ...rest } = options ?? {};
        return {
          headers: region ? { 'x-region': region } : undefined,
          method: 'GET',
          params: { asins: asin, ...rest, no_cache: 1 },
          url: '/products',
        };
      },
    }),
    getProductById: builder.query<GetProductByIdResBody, GetProductByIdReqBody>({
      query: ({
        id,
        options,
      }) => {
        const { region, ...rest } = options ?? {};
        return {
          headers: region ? { 'x-region': region } : undefined,
          method: 'GET',
          params: { w_items_id: id, ...rest, no_cache: 1 },
          url: '/walmart/products',
        };
      },
    }),
    getProductContentCopies: builder.query<GetProductContentCopiesResBody, string | string[]>({
      query: (asins) => ({
        method: 'GET',
        params: { asins },
        url: '/products/copy-entries',
      }),
    }),
    submitProductContent: builder.mutation<SubmitProductContentResBody, SubmitProductContentReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/agg-pages/products/content',
      }),
    }),
    updateProduct: builder.mutation<null, UpdateProductReqBody>({
      query: ({
        data: body,
        product_id,
      }) => ({
        body,
        method: 'PUT',
        url: `/admin/products/update/${product_id}`,
      }),
    }),
    updateProductReviews: builder.mutation<null, UpdateProductReviewsReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/products/reviews',
      }),
    }),
    getFullProductData: builder.query<Omit<PageFormProduct, 'marketplace'>, GetFullProductDataReqBody>({
      async queryFn({
        id,
        marketplace,
        options,
      }, { dispatch }) {
        try {
          const isAmazon = marketplace === 'amazon';
          const { getProductByAsin, getProductById } = productsApiSlice.endpoints;

          let products;

          if (isAmazon) {
            const amazonResponse = await dispatch(getProductByAsin.initiate({ asin: String(id), options })).unwrap();
            products = amazonResponse?.data?.products;
          } else {
            const walmartResponse = await dispatch(getProductById.initiate({ id: Number(id), options })).unwrap();
            products = walmartResponse?.products;
          }

          if (products?.length > 0) {
            const product: Product = {
              ...products[0],
              product_id: isAmazon ? String(products[0]?.asin)
                : String(products[0]?.w_item_id),
            };

            const { getProductLinks } = productLinksApiSlice.endpoints;
            const linksResponse = await dispatch(
              getProductLinks.initiate({
                asins: isAmazon ? [String(id)] : [],
                w_item_ids: !isAmazon ? [String(id)] : [],
                region: options?.region || 'us',
              }),
            ).unwrap();

            const linkedProductData = linksResponse.data?.[String(id)] || null;

            const productAiImages: IProductAiImages['ids'][number] = {
              ai_image_url: product?.ai_images?.img_large || product?.ai_images?.img_medium || '',
              product_id: product.product_id,
              regenerate: false,
              title: product.title || '',
            };

            const {
              assets,
              reviews,
            } = utilProductDataPreparing(product);

            const mappedContent: PageFormProduct['content'] = {
              _id: `${product.asin}-origin`,
              product_id: product.product_id,
              type: 'origin',
              tied: true,
              bottomLine: product.content?.bottomLine || '',
              summary: product.content?.summary || '',
              faq: product.content?.faq || [],
              pros: product.content?.pros || [],
            };

            const result: PageFormProduct = {
              product_marketplace: product?.product_marketplace,
              original_id: product.id,
              page_product_id: product?.page_product_id,
              product_id: product.product_id,
              title: product.title || '',
              subtitle: product.subtitle || '',
              pickedProductCopyId: product.picked_product_copy_id,
              value: '',
              imgMedium: product.img_medium || '',
              imgLarge: product.img_large || '',
              price: product.price || 0,
              isHardcodedLink: product.is_hardcoded_link || 0,
              pageUrl: product.page_url || '',
              hardcodedLinkSource: product.hardcoded_link_source ?? null,
              order: 0,
              status: 'new',
              aiImages: productAiImages,
              content: mappedContent,
              reviews: {
                product_id: product.product_id,
                reviews: Array.isArray(reviews.reviews) ? (reviews.reviews as IProductReview[]) : [],
              },
              productAssets: Array.isArray(assets) ? assets : [],
              linked_product_id: linkedProductData ? linkedProductData.product_id : null,
            };

            return { data: result };
          }

          return {
            error: {
              status: 404,
              data: 'Product not found in database',
            } as FetchBaseQueryError,
          };
        } catch (e: unknown) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: e instanceof Error ? e.message : 'Unknown error occurred',
            } as FetchBaseQueryError,
          };
        }
      },
    }),
  }),
  reducerPath: 'productsApi',
});
